/**
 * SmartEventSource - A wrapper that mimics EventSource API but falls back to polling
 * if SSE connection fails to establish within a timeout period
 */
export class SmartEventSource {
    constructor(url, options = {}) {
        this.url = url;
        this.options = options;
        this.readyState = 0; // CONNECTING
        this.listeners = new Map();
        this.eventSource = null;
        this.pollingInterval = null;
        this.lastTimestamp = new Date().toISOString();
        this.isClosed = false;
        this.pollDelay = 2000; // Poll every 2 seconds.
        this.isPollingMode = false; // Track if we're using polling for messages
        // Extract cid from URL for history polling
        const urlParams = new URLSearchParams(url.split('?')[1]);
        this.cid = urlParams.get('cid');
        // Extract base URL for history endpoint
        this.baseUrl = url.split('/api/')[0];
        // Check for force polling flag (from URL params or global setting)
        this.forcePolling = urlParams.get('forcePolling') === 'true' || window.FORCE_SMART_EVENTSOURCE_POLLING === true;
        
        if (this.forcePolling) {
            console.log('[SmartEventSource] Force polling mode enabled');
            // Still init SSE to keep connection open
            this.initSSE();
            // Immediately switch to polling mode
            this.fallbackToPolling();
        } else {
            // Start with SSE normally
            this.initSSE();
        }
    }
    
    initSSE() {
        console.log('[SmartEventSource] Attempting SSE connection...');
        
        // Create the EventSource
        this.eventSource = new EventSource(this.url, this.options);
        
        // Set up a timeout to check if we received an 'open' event
        const sseTimeout = setTimeout(() => {
            if (this.readyState === 0) { // Still connecting
                console.log('[SmartEventSource] SSE timeout - falling back to polling');
                this.fallbackToPolling();
            }
        }, 2000);
        
        // Forward all EventSource events
        this.eventSource.onopen = (event) => {
            clearTimeout(sseTimeout);
            this.readyState = 1; // OPEN
            console.log('[SmartEventSource] SSE connection established');
            // Only dispatch if not already in polling mode
            if (!this.isPollingMode) {
                this.dispatchEvent('open', event);
            }
        };
        
        this.eventSource.onmessage = (event) => {
            // Only dispatch messages if we're not in polling mode
            if (!this.isPollingMode) {
                this.dispatchEvent('message', event);
            }
        };
        
        this.eventSource.onerror = (event) => {
            clearTimeout(sseTimeout);
            console.log('[SmartEventSource] SSE error - falling back to polling');
            // Only dispatch error if not already in polling mode
            if (!this.isPollingMode) {
                this.dispatchEvent('error', event);
            }
            this.fallbackToPolling();
        };
        
        // Set up specific event listeners
        this.setupSSEListeners();
    }
    
    fallbackToPolling() {
        console.log('[SmartEventSource] Switching to polling mode while keeping SSE connection open');
        // Keep SSE connection open - do NOT close it
        // We'll use it just to maintain the connection, not for messages
        this.isPollingMode = true;
        // Mark as open (polling is active)
        this.readyState = 1;
        // Dispatch open event for polling mode
        this.dispatchEvent('open', { type: 'open', data: 'Polling mode active' });
        // Start polling
        this.startPolling();
    }
    
    async startPolling() {
        if (this.isClosed) return;
        
        const poll = async () => {
            if (this.isClosed || this.readyState !== 1) return;
            
            try {
                const response = await fetch(`${this.baseUrl}/api/v2/assistants/history?cid=${this.cid}&timestamp=${encodeURIComponent(this.lastTimestamp)}`);
                
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                
                const data = await response.json();
                
                // Process each message
                if (data.messages && data.messages.length > 0) {
                    for (const msg of data.messages) {
                        // Update last timestamp
                        if (msg.timestamp) {
                            this.lastTimestamp = msg.timestamp;
                        }
                        
                        // Convert to EventSource-like format
                        const event = {
                            type: 'message',
                            data: typeof msg.data === 'string' ? msg.data : JSON.stringify(msg.data),
                            origin: this.url,
                            lastEventId: msg.timestamp || ''
                        };
                        
                        // Handle special event types
                        if (msg.event) {
                            // For special events like 'done', 'agenda', etc.
                            this.dispatchEvent(msg.event, {
                                type: msg.event,
                                data: event.data,
                                origin: event.origin,
                                lastEventId: event.lastEventId
                            });
                        } else {
                            // Regular message event
                            this.dispatchEvent('message', event);
                        }
                    }
                }
                
            } catch (error) {
                console.error('[SmartEventSource] Polling error:', error);
                this.dispatchEvent('error', { type: 'error', error });
            }
            
            // Schedule next poll if not closed
            if (!this.isClosed && this.readyState === 1) {
                this.pollingInterval = setTimeout(poll, this.pollDelay);
            }
        };
        
        // Start polling immediately
        poll();
    }
    
    setupSSEListeners() {
        if (!this.eventSource) return;
        
        // Set up listeners for any specific events that were registered
        for (const [eventType] of this.listeners) {
            if (eventType !== 'open' && eventType !== 'message' && eventType !== 'error') {
                this.eventSource.addEventListener(eventType, (event) => {
                    // Only dispatch events if we're not in polling mode
                    if (!this.isPollingMode) {
                        this.dispatchEvent(eventType, event);
                    }
                });
            }
        }
    }
    
    addEventListener(type, handler) {
        if (!this.listeners.has(type)) {
            this.listeners.set(type, []);
        }
        this.listeners.get(type).push(handler);
        
        // If we already have an EventSource and not in polling mode, add the listener to it
        if (this.eventSource && !this.isPollingMode && type !== 'open' && type !== 'message' && type !== 'error') {
            this.eventSource.addEventListener(type, handler);
        }
    }
    
    removeEventListener(type, handler) {
        if (this.listeners.has(type)) {
            const handlers = this.listeners.get(type);
            const index = handlers.indexOf(handler);
            if (index !== -1) {
                handlers.splice(index, 1);
            }
        }
        
        // Remove from EventSource if it exists
        if (this.eventSource) {
            this.eventSource.removeEventListener(type, handler);
        }
    }
    
    dispatchEvent(type, event) {
        const handlers = this.listeners.get(type) || [];
        for (const handler of handlers) {
            handler(event);
        }
        
        // Also check for on* properties
        const onHandler = this[`on${type}`];
        if (typeof onHandler === 'function') {
            onHandler(event);
        }
    }
    
    close() {
        console.log('[SmartEventSource] Closing connection');
        this.isClosed = true;
        this.readyState = 2; // CLOSED
        
        // Clear polling interval
        if (this.pollingInterval) {
            clearTimeout(this.pollingInterval);
            this.pollingInterval = null;
        }
        
        // Close EventSource if it exists
        if (this.eventSource) {
            this.eventSource.close();
            this.eventSource = null;
        }
    }
    
    // EventSource compatibility properties
    get CONNECTING() { return 0; }
    get OPEN() { return 1; }
    get CLOSED() { return 2; }
}

export const useSmartEventSource = () => {
    return {
        SmartEventSource
    };
};